import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { API, SITE } from "@/lib/site";
import useSeo from "@/hooks/useSeo";

export default function DeleteVerify() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "loading", message: "", reference: null });
  useSeo({ title: "Confirm account deletion — Orrbbit", description: "Confirm your Orrbbit account deletion request." });

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setState({ status: "error", message: "This confirmation link is missing its token." });
      return;
    }
    axios.post(`${API}/deletion-requests/verify`, { token })
      .then((res) => setState({ status: "ok", message: res.data.message, reference: res.data.reference }))
      .catch((e) => setState({ status: "error", message: e.response?.data?.detail || "This confirmation link is invalid or has expired." }));
  }, [params]);

  return (
    <div data-testid="delete-verify-page" className="flex min-h-[70vh] items-center justify-center bg-lightbg">
      <div className="container-x max-w-lg text-center">
        <div className="rounded-3xl border border-border bg-white p-10 shadow-soft">
          {state.status === "loading" && (
            <>
              <Loader2 className="mx-auto animate-spin text-teal" size={40} />
              <p className="mt-4 text-bodygrey">Confirming your request…</p>
            </>
          )}
          {state.status === "ok" && (
            <div data-testid="verify-success">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-teal/15 text-teal"><CheckCircle2 size={34} /></span>
              <h1 className="mt-5 font-heading text-2xl font-semibold text-navy">Deletion request confirmed</h1>
              <p className="mt-3 text-bodygrey">{state.message}</p>
              {state.reference && <p className="mt-2 text-sm text-bodygrey">Reference: <span className="font-mono font-semibold text-teal">{state.reference}</span></p>}
              <Link to="/" className="mt-6 inline-block rounded-full bg-teal px-6 py-2.5 font-semibold text-white transition-transform hover:scale-105">Back to home</Link>
            </div>
          )}
          {state.status === "error" && (
            <div data-testid="verify-error">
              <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500"><XCircle size={34} /></span>
              <h1 className="mt-5 font-heading text-2xl font-semibold text-navy">We couldn't confirm this link</h1>
              <p className="mt-3 text-bodygrey">{state.message}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link to="/delete-account" className="rounded-full bg-teal px-6 py-2.5 font-semibold text-white transition-transform hover:scale-105">Start a new request</Link>
                <a href={`mailto:${SITE.supportEmail}`} className="rounded-full border border-border px-6 py-2.5 font-semibold text-navy hover:bg-lightbg">Contact support</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

